// src/services/teams.js
// ---------------------------------------------------------------------------
// Services "Teams" : appels HTTP vers l'API Django/DRF.
// Hypothèses :
// - api/axios est configuré avec baseURL, withCredentials, X-Tenant et X-CSRFToken.
// - L'API DRF expose les routes sous /teams/ ... selon TeamViewSet (RetrieveModelMixin inclus).
// - Les endpoints peuvent renvoyer soit un array brut, soit un objet paginé { results: [...] }.
//   On normalise le retour pour renvoyer *toujours* un array côté liste.
// ---------------------------------------------------------------------------

import api from "api/axios";

/**
 * Liste les teams visibles par l'utilisateur courant (membre uniquement).
 * GET /teams/
 * @returns {Promise<Array>} Tableau de teams [{ id, name, description, is_active }, ...]
 */
export async function listMyTeams() {
  const res = await api.get("/teams/");
  const data = res.data;
  // Support des deux formats (DRF simple ou DRF paginé)
  return Array.isArray(data) ? data : (data?.results ?? []);
}

/**
 * Crée une team dans le tenant courant.
 * POST /teams/
 * @param {{name: string, description?: string, is_active?: boolean}} payload
 * @returns {Promise<Object>} Team créée { id, name, description, is_active, ... }
 */
export async function createTeam(payload) {
  const res = await api.post("/teams/", payload);
  return res.data;
}

/**
 * Récupère le détail d'une team (incluant ses membres si le backend sérialise "members").
 * GET /teams/:id/
 * @param {number|string} teamId
 * @returns {Promise<Object>} Team { id, name, description, is_active, members?: [...] }
 */
export async function getTeam(teamId) {
  const res = await api.get(`/teams/${teamId}/`);
  return res.data;
}

/**
 * Liste les membres d'une team.
 * GET /teams/:id/members/
 * @param {number|string} teamId
 * @returns {Promise<Array>} Tableau de memberships [{ id, role_level, created_at, user: {id, username, email} }, ...]
 */
export async function listTeamMembers(teamId) {
  const res = await api.get(`/teams/${teamId}/members/`);
  const data = res.data;
  // Normalise en array même si l'API est paginée
  return Array.isArray(data) ? data : (data?.results ?? []);
}

/**
 * Retourne mon appartenance (niveau) dans la team cible.
 * GET /teams/:id/my-membership/
 * @param {number|string} teamId
 * @returns {Promise<{id:number, role_level:"ED"|"GA"|"SUP"|"PD"} | null>}
 * Renvoie null si 404 (non-membre), relance l'erreur sinon.
 */
export async function myMembershipForTeam(teamId) {
  try {
    const res = await api.get(`/teams/${teamId}/my-membership/`);
    return res.data; // { id, role_level }
  } catch (err) {
    // Cas attendu : l'utilisateur n'est pas membre -> 404 => renvoyer null sans throw
    if (err?.response?.status === 404) return null;
    // Autres cas : réseau, 5xx, 4xx non prévu -> laisser remonter
    throw err;
  }
}

/**
 * Ajoute (ou upgrade) un membre dans la team donnée, selon la règle pyramidale.
 * POST /teams/:id/members/
 * @param {number|string} teamId
 * @param {number} userId ID du user à ajouter
 * @param {"ED"|"GA"|"SUP"|"PD"} roleLevel Niveau à affecter
 * @returns {Promise<Object>} Membership créé/MAJ (format lecture, avec user embarqué)
 */
export async function addMember(teamId, userId, roleLevel) {
  const res = await api.post(`/teams/${teamId}/members/`, {
    user_id: userId,
    role_level: roleLevel,
  });
  return res.data;
}

/**
 * Supprime un membre de la team.
 * DELETE /teams/:id/members/:membershipId/
 * @param {number|string} teamId
 * @param {number|string} membershipId
 * @returns {Promise<boolean>} true si succès (même si 204 No Content)
 */
export async function removeMember(teamId, membershipId) {
  await api.delete(`/teams/${teamId}/members/${membershipId}/`);
  // La plupart des DELETE DRF renvoient 204 No Content, donc pas de res.data à exploiter.
  // On normalise le succès en renvoyant true.
  return true;
}

/**
 * Recherche des utilisateurs éligibles (non membres) pour une team.
 * GET /teams/:id/eligible-users/?q=...&limit=...
 * @returns {Promise<Array<{id:number, username:string, email:string}>>}
 */
export async function searchEligibleUsers(teamId, q = "", limit = 20) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (limit) params.set("limit", String(limit));
  const res = await api.get(`/teams/${teamId}/eligible-users/?${params.toString()}`);
  const data = res.data;
  return Array.isArray(data) ? data : (data?.results ?? []);
}

/**
 * Ajout en masse de membres.
 * POST /teams/:id/members/batch/
 * body: { user_ids: number[], role_level: "ED"|"GA"|"SUP"|"PD" }
 * @returns {Promise<Array>} memberships créés/mis à jour (format lecture)
 */
export async function addMembersBulk(teamId, userIds, roleLevel) {
  const res = await api.post(`/teams/${teamId}/members/batch/`, {
    user_ids: userIds,
    role_level: roleLevel,
  });
  return Array.isArray(res.data) ? res.data : (res.data?.results ?? []);
}
